/**
 * Procedural ambient sailing audio.
 *
 * Everything is synthesised from white noise + oscillators through the Web Audio
 * API — no external audio files. Three layers:
 *
 *   1. Wind   — bandpassed white noise whose loudness/brightness follows the
 *               authoritative `windStrength`.
 *   2. Waves  — lowpassed noise, its volume slowly modulated by a ~0.3 Hz LFO,
 *               scaled by the (debug-tunable) wave amplitude and wind.
 *   3. Creaks — occasional short bandpassed noise bursts (wood under stress),
 *               triggered more often at higher speed / rougher seas.
 *
 * The graph starts silent and fades in on the first user gesture to satisfy the
 * browser autoplay policy. `update()` is meant to be called every frame from the
 * game loop; `dispose()` tears everything down.
 */

export interface AudioAmbience {
  /** Authoritative wind strength, roughly 0.15 .. 1.0. */
  windStrength: number;
  /** Ship speed in m/s. */
  shipSpeed: number;
  /** Debug-tunable wave amplitude multiplier (default 1). */
  waveAmplitude: number;
  /** True while the wreck/defeat overlay is up. */
  defeated?: boolean;
}

const NOISE_SECONDS = 3;
const FADE_IN_SECONDS = 2.5;
const MAX_SHIP_SPEED = 9;
const MAX_WAVE_AMPLITUDE = 1.5;
const DEFAULT_MASTER_VOLUME = 0.7;

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

export class AudioSystem {
  private context: AudioContext | null = null;
  private unlocked = false;
  private disposed = false;

  private noiseBuffer: AudioBuffer | null = null;
  private masterGain: GainNode | null = null;
  private fadeGain: GainNode | null = null;

  private windSource: AudioBufferSourceNode | null = null;
  private windFilter: BiquadFilterNode | null = null;
  private windGain: GainNode | null = null;

  private waveSource: AudioBufferSourceNode | null = null;
  private waveFilter: BiquadFilterNode | null = null;
  private waveGain: GainNode | null = null;
  private waveLfo: OscillatorNode | null = null;
  private waveLfoGain: GainNode | null = null;

  private masterVolume = DEFAULT_MASTER_VOLUME;
  private creakTimer = 3;

  private readonly onFirstGesture = () => {
    void this.unlock();
  };

  constructor() {
    window.addEventListener('pointerdown', this.onFirstGesture, { once: true });
    window.addEventListener('keydown', this.onFirstGesture, { once: true });
    window.addEventListener('touchstart', this.onFirstGesture, { once: true });
  }

  /** Create the audio graph on the first user gesture (autoplay policy). */
  async unlock(): Promise<void> {
    if (this.unlocked || this.disposed) return;
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    const context = new AudioContextClass();
    this.context = context;
    try {
      await context.resume();
    } catch {
      // A suspended context still renders once resumed; bake the graph anyway.
    }
    if (this.disposed) {
      void context.close();
      this.context = null;
      return;
    }
    this.buildGraph(context);
    this.unlocked = true;
  }

  private buildGraph(context: AudioContext): void {
    this.noiseBuffer = this.createNoiseBuffer();

    this.masterGain = context.createGain();
    this.masterGain.gain.value = this.masterVolume;

    // Global fade applied after the master volume so the first gesture always
    // ramps the whole mix up from silence.
    this.fadeGain = context.createGain();
    this.fadeGain.gain.value = 0;
    this.masterGain.connect(this.fadeGain);
    this.fadeGain.connect(context.destination);

    // --- Wind: bandpassed white noise -------------------------------------
    this.windFilter = context.createBiquadFilter();
    this.windFilter.type = 'bandpass';
    this.windFilter.frequency.value = 500;
    this.windFilter.Q.value = 0.6;
    this.windGain = context.createGain();
    this.windGain.gain.value = 0;
    this.windSource = this.createNoiseSource(context);
    this.windSource.connect(this.windFilter);
    this.windFilter.connect(this.windGain);
    this.windGain.connect(this.masterGain);
    this.windSource.start();

    // --- Waves: lowpassed noise with a slow LFO swell ---------------------
    this.waveFilter = context.createBiquadFilter();
    this.waveFilter.type = 'lowpass';
    this.waveFilter.frequency.value = 220;
    this.waveFilter.Q.value = 0.4;
    this.waveGain = context.createGain();
    this.waveGain.gain.value = 0;
    this.waveSource = this.createNoiseSource(context);
    this.waveSource.connect(this.waveFilter);
    this.waveFilter.connect(this.waveGain);
    this.waveGain.connect(this.masterGain);
    this.waveSource.start();

    // ~0.3 Hz sine modulating the wave gain: the periodic "set" of the swell.
    this.waveLfo = context.createOscillator();
    this.waveLfo.type = 'sine';
    this.waveLfo.frequency.value = 0.3;
    this.waveLfoGain = context.createGain();
    this.waveLfoGain.gain.value = 0;
    this.waveLfo.connect(this.waveLfoGain);
    this.waveLfoGain.connect(this.waveGain.gain);
    this.waveLfo.start();

    // Fade the mix in from silence.
    const now = context.currentTime;
    this.fadeGain.gain.cancelScheduledValues(now);
    this.fadeGain.gain.setValueAtTime(0, now);
    this.fadeGain.gain.linearRampToValueAtTime(1, now + FADE_IN_SECONDS);
  }

  /** Per-frame update; safe to call before the context exists. */
  update(delta: number, ambience: AudioAmbience): void {
    const context = this.context;
    if (!context || !this.unlocked) return;
    const now = context.currentTime;

    const defeated = ambience.defeated === true;
    const wind = clamp01(ambience.windStrength);
    const speed = clamp01(ambience.shipSpeed / MAX_SHIP_SPEED);
    const wave = clamp01(ambience.waveAmplitude / MAX_WAVE_AMPLITUDE);
    const scale = defeated ? 0.5 : 1;

    // Wind: louder and brighter with strength.
    if (this.windGain) {
      this.windGain.gain.setTargetAtTime((0.025 + wind * 0.16) * scale, now, 0.5);
    }
    if (this.windFilter) {
      this.windFilter.frequency.setTargetAtTime(300 + wind * 500, now, 0.5);
    }

    // Waves: more energy in rough seas and strong wind.
    const waveBase = (0.015 + wave * (0.05 + wind * 0.06)) * scale;
    if (this.waveGain) {
      this.waveGain.gain.setTargetAtTime(waveBase, now, 0.6);
    }
    if (this.waveFilter) {
      this.waveFilter.frequency.setTargetAtTime(110 + wave * 170, now, 0.6);
    }
    if (this.waveLfo) {
      this.waveLfo.frequency.setTargetAtTime(0.22 + wind * 0.16, now, 0.8);
    }
    if (this.waveLfoGain) {
      this.waveLfoGain.gain.setTargetAtTime(waveBase * 0.8, now, 0.6);
    }

    if (!defeated) this.updateCreaks(delta, speed, wave, now);
  }

  private updateCreaks(delta: number, speed: number, wave: number, now: number): void {
    const stress = clamp01(speed * 0.8 + wave * 0.6);
    this.creakTimer -= delta;
    if (this.creakTimer > 0) return;

    // Calm seas: a rare groan every ~10s. Rough and fast: every few seconds.
    const meanInterval = 14 - stress * 11;
    this.creakTimer = meanInterval * (0.6 + Math.random() * 0.9);
    if (Math.random() < 0.2 + stress * 0.55) this.playCreak(stress, now);
  }

  private playCreak(stress: number, now: number): void {
    const context = this.context;
    const buffer = this.noiseBuffer;
    if (!context || !buffer || !this.masterGain) return;

    const source = context.createBufferSource();
    source.buffer = buffer;
    const filter = context.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 800 + Math.random() * 1200; // 800-2000 Hz
    filter.Q.value = 6 + Math.random() * 8;
    const gain = context.createGain();

    const peak = 0.05 + stress * 0.12;
    const duration = 0.18 + Math.random() * 0.35;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(peak, now + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);
    source.start(now, Math.random() * buffer.duration);
    source.stop(now + duration + 0.05);
  }

  setMasterVolume(volume: number): void {
    this.masterVolume = clamp01(volume);
    const context = this.context;
    if (!context || !this.masterGain) return;
    const now = context.currentTime;
    this.masterGain.gain.cancelScheduledValues(now);
    this.masterGain.gain.setTargetAtTime(this.masterVolume, now, 0.05);
  }

  getMasterVolume(): number {
    return this.masterVolume;
  }

  /** The shared AudioContext, or null before the first user gesture unlocks audio. */
  getContext(): AudioContext | null {
    return this.context;
  }

  /**
   * The pre-fade master gain. Storm layers connect here so they inherit both
   * the master volume and the first-gesture fade-in.
   */
  getMasterGain(): GainNode | null {
    return this.masterGain;
  }

  dispose(): void {
    this.disposed = true;
    this.unlocked = false;
    window.removeEventListener('pointerdown', this.onFirstGesture);
    window.removeEventListener('keydown', this.onFirstGesture);
    window.removeEventListener('touchstart', this.onFirstGesture);

    const stop = (node: AudioScheduledSourceNode | null) => {
      if (!node) return;
      try {
        node.stop();
      } catch {
        // Already stopped or never started.
      }
      node.disconnect();
    };
    stop(this.windSource);
    stop(this.waveSource);
    stop(this.waveLfo);

    this.windSource = null;
    this.waveSource = null;
    this.waveLfo = null;
    this.windFilter?.disconnect();
    this.windGain?.disconnect();
    this.waveFilter?.disconnect();
    this.waveGain?.disconnect();
    this.waveLfoGain?.disconnect();
    this.masterGain?.disconnect();
    this.fadeGain?.disconnect();
    this.windFilter = null;
    this.windGain = null;
    this.waveFilter = null;
    this.waveGain = null;
    this.waveLfoGain = null;
    this.masterGain = null;
    this.fadeGain = null;
    this.noiseBuffer = null;

    const context = this.context;
    this.context = null;
    if (context && context.state !== 'closed') void context.close();
  }

  /**
   * Create a mono white-noise buffer of the requested length in seconds, or
   * null before the context exists. Exposed so storm audio can build clips.
   */
  createNoiseBuffer(seconds = NOISE_SECONDS): AudioBuffer | null {
    const context = this.context;
    if (!context) return null;
    const length = Math.max(1, Math.floor(context.sampleRate * seconds));
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = Math.random() * 2 - 1;
    return buffer;
  }

  private createNoiseSource(context: AudioContext): AudioBufferSourceNode {
    const source = context.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.loop = true;
    return source;
  }
}
